import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import { Carousel } from '@heroui-pro/react';
import {
  Alert,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerHeading,
  DrawerTrigger,
  Input,
  Label,
  Slider,
  Spinner,
  Tabs,
  TabList,
  TabPanel,
  Tab,
  toast,
} from '@heroui/react';
import { formatDateTime } from '@his/shared/utils/formatTime';
import { useWeChatShare } from '@his/shared';
import { LotteryRulesTimeline } from '@/components/activities/LotteryRulesTimeline';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { Gender } from './types';
import { APP_CONFIG } from './constants';
import HistoryChart from './components/HistoryChart';
import chinaFlag from '../../../../../../packages/ui/src/assets/images/flag/china_flag.svg?url';
import germanFlag from '../../../../../../packages/ui/src/assets/images/flag/german-flag.svg?url';
import { IconMedal as Medal, IconVolume as Volume2, IconVolumeOff as VolumeX } from '@tabler/icons-react';

const INVITER_REQUIRED_MSG = '缺少邀请人，无法参加活动，请联系您身边的安联寿险规划师。';

/** 中国大陆 11 位手机号：1 开头，第二位 3-9，共 11 位数字 */
const CN_MOBILE_REGEX = /^1[3-9]\d{9}$/;

/** 提交硬截止时间：北京时间 2026-02-23 00:00:00，超过后禁用提交 */
function isValidCnMobile(value: string): boolean {
  return CN_MOBILE_REGEX.test(value.trim());
}

/** AI 预测中德两国 2026 冬奥会金牌数（表格数据） */
const AI_PREDICTION_ROWS = [
  { ai: '豆包', china: '5-7枚', germany: '12-14枚' },
  { ai: 'Deepseek', china: '5-8枚', germany: '10-13枚' },
  { ai: '阿里千问', china: '5-7枚', germany: '10-12枚' },
  { ai: 'chatGPT', china: '6-10枚', germany: '10-15枚' },
  { ai: 'Grok', china: '6-8枚', germany: '20-22枚' },
];

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}年${month}月${day}日`;
}

/** 格式化为 YYYY-MM-DD HH:MM（分钟级精度，用于抽奖规则分组与展示） */
function formatDateTimeMinute(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * 2026冬奥金牌猜想页面
 * 
 * 使用 HeroUI 组件构建移动端界面
 */
function genderToApi(g: Gender): 'MALE' | 'FEMALE' {
  return g === Gender.MALE ? 'MALE' : 'FEMALE';
}

/** 姓名列展示：取 name 首字 + 性别（先生/女士） */
function nameWithGenderDisplay(name: string | undefined | null, gender: string | undefined | null): string {
  const first = (name ?? '').trim()[0];
  if (!first) return '—';
  const suffix = gender === 'MALE' ? '先生' : gender === 'FEMALE' ? '女士' : '';
  return first + suffix;
}

export default function OlympicGuessPage() {
  const [searchParams] = useSearchParams();
  const activityId = searchParams.get('activity') ?? '';
  const inviterId = searchParams.get('inviter') ?? '';

  // 获取活动详情
  const { data: activity } = trpc.activities.get.useQuery(
    { id: activityId },
    {
      enabled: !!activityId,
    }
  );

  // 抽奖规则 Tab：奖品与规则（与 LotteryRulesPage 一致，活动 ID 来自 URL）
  const { data: prizes, isLoading: prizesLoading } = trpc.activityPrizes.listAll.useQuery(
    { activityId: activityId || '' },
    { enabled: !!activityId }
  );
  const { data: lotteryRules, isLoading: rulesLoading } = trpc.activityLotteryRules.listAll.useQuery(
    {
      activityId: activityId || '',
      sortBy: 'lotteryTime',
      sortOrder: 'asc', // 从远到近
    },
    { enabled: !!activityId }
  );
  const prizesAutoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: true }));

  // 获奖名单：分页与手机号搜索
  const [winnersPage, setWinnersPage] = useState(1);
  const winnersPageSize = 30;
  const [winnersKeyword, setWinnersKeyword] = useState('');
  const [winnersKeywordInput, setWinnersKeywordInput] = useState('');
  const winnersQueryInput = React.useMemo(
    () => ({
      activityId: activityId || '',
      isWinner: true as const,
      where: {},
      keyword: winnersKeyword.trim() || undefined,
      page: winnersPage,
      pageSize: winnersPageSize,
      sortBy: 'createdAt' as const,
      sortOrder: 'desc' as const,
    }),
    [activityId, winnersKeyword, winnersPage]
  );
  const { data: winnersData, isLoading: winnersLoading } =
    trpc.activityRegistrations.list.useQuery(winnersQueryInput, {
      enabled: !!activityId,
    });
  const winnersItems = winnersData?.items ?? [];
  const winnersTotal = winnersData?.total ?? 0;
  const winnersHasPrev = winnersData?.hasPrev ?? false;
  const winnersHasNext = winnersData?.hasNext ?? false;
  const winnersTotalPages = winnersData?.totalPages ?? 1;

  // 按手机号查询该活动下是否有一条报名（不筛 isWinner），用于区分：未注册 / 已报名未中奖 / 已中奖
  const winnersKeywordTrimmed = winnersKeyword.trim();
  const winnersLookupInput = React.useMemo(
    () =>
      winnersKeywordTrimmed
        ? {
            activityId: activityId || '',
            isWinner: undefined as unknown,
            where: {},
            keyword: winnersKeywordTrimmed,
            pageSize: 1,
            sortBy: 'createdAt' as const,
            sortOrder: 'desc' as const,
          }
        : null,
    [activityId, winnersKeywordTrimmed]
  );
  const { data: winnersLookupData, isLoading: winnersLookupLoading } =
    trpc.activityRegistrations.list.useQuery(winnersLookupInput ?? { activityId: '', isWinner: undefined as unknown, where: {}, pageSize: 1 }, {
      enabled: !!activityId && !!winnersLookupInput,
    });

  type SearchResultState = 'loading' | 'not_registered' | 'not_winner' | 'winner' | null;
  const winnersSearchResultState: SearchResultState = React.useMemo(() => {
    if (!winnersKeywordTrimmed) return null;
    if (winnersLookupLoading) return 'loading';
    if (!winnersLookupData?.items?.length) return 'not_registered';
    const item = winnersLookupData.items[0] as { isWinner?: boolean };
    if (item.isWinner === true) return 'winner';
    return 'not_winner';
  }, [winnersKeywordTrimmed, winnersLookupLoading, winnersLookupData?.items]);

  const handleWinnersSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = winnersKeywordInput.trim();
    if (trimmed === '') {
      setWinnersKeyword('');
      setWinnersPage(1);
      return;
    }
    if (!isValidCnMobile(trimmed)) {
      toast.error('请输入正确的 11 位中国手机号');
      return;
    }
    setWinnersKeyword(trimmed);
    setWinnersPage(1);
  };

  const groupedRules = React.useMemo(() => {
    if (!lotteryRules || !prizes) return [];
    const prizeMap = new Map(prizes.map((p) => [p.id, p]));
    const groups = new Map<string, typeof lotteryRules>();
    lotteryRules.forEach((rule) => {
      if (!rule.lotteryTime) return;
      const timeKey = formatDateTimeMinute(rule.lotteryTime);
      if (!groups.has(timeKey)) groups.set(timeKey, []);
      groups.get(timeKey)!.push(rule);
    });
    return Array.from(groups.entries())
      .map(([_, rules]) => {
        const first = rules[0];
        const displayTime = first?.lotteryTime ? formatDateTimeMinute(first.lotteryTime) : '';
        const mappedRules = rules.map((rule) => ({
          ...rule,
          prize: rule.prizeId ? prizeMap.get(rule.prizeId) : null,
        }));
        const allCompleted = mappedRules.every((r) => r.isCompleted === true);
        return {
          time: displayTime,
          rules: mappedRules,
          allCompleted,
        };
      })
      .sort((a, b) => {
        const timeA = a.rules[0]?.lotteryTime ? new Date(a.rules[0].lotteryTime).getTime() : 0;
        const timeB = b.rules[0]?.lotteryTime ? new Date(b.rules[0].lotteryTime).getTime() : 0;
        // 从远到近（时间小的在前）
        return timeA - timeB;
      });
  }, [lotteryRules, prizes]);

  // Form State
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender>(Gender.MALE);
  const [phone, setPhone] = useState('');
  const [chinaGolds, setChinaGolds] = useState(12);
  const [germanyGolds, setGermanyGolds] = useState(12);

  // UI State（不再用 hasSubmitted 阻止重复提交，活动未到期可多次提交）
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'guess' | 'rules' | 'winners'>('guess');
  const [isPlaying, setIsPlaying] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  // 设置浏览器标题为活动标题
  useEffect(() => {
    if (activity?.title) {
      document.title = activity.title;
    }
  }, [activity?.title]);

  // 微信分享配置
  const { ready: wxReady, init: wxInit, setShareInfo } = useWeChatShare();

  useEffect(() => {
    wxInit();
  }, [wxInit]);

  useEffect(() => {
    if (wxReady && activity) {
      // 从富文本中提取纯文本作为分享描述，截取前 60 字符
      const plainDesc = activity.description
        ? activity.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().slice(0, 60)
        : '';
      // 分享图片：优先用户头像，其次活动封面
      const shareImg = user?.avatarUrl || activity.coverImage || '';

      setShareInfo({
        title: activity.title ?? '',
        desc: plainDesc,
        link: window.location.href,
        imgUrl: shareImg,
      });
    }
  }, [wxReady, activity, user?.avatarUrl, setShareInfo]);

  // 活动时间判断：未开始 / 已结束则禁用提交（不再通过固定硬截止时间控制）
  const now = Date.now();
  const startTimeMs = activity?.startTime ? new Date(activity.startTime).getTime() : null;
  const endTimeMs = activity?.endTime ? new Date(activity.endTime).getTime() : null;
  const notStarted = startTimeMs != null && now < startTimeMs;
  const ended = endTimeMs != null && now > endTimeMs;
  const submitDisabledByTime = notStarted || ended;

  // 错误由 createProviders 的 MutationCache.onError 统一处理（handleError + onError），
  // 不再在此 onError 中 toasting，避免与「Unable to transform」等 tRPC 解析错误双条 toasts。
  const createByInvitation = trpc.activityRegistrations.createByInvitation.useMutation();

  // Audio Refs
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const clickSfxRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    bgMusicRef.current = new Audio(APP_CONFIG.BG_MUSIC_URL);
    bgMusicRef.current.loop = true;
    clickSfxRef.current = new Audio(APP_CONFIG.CLICK_SFX);
  }, []);

  const toggleMusic = () => {
    if (bgMusicRef.current) {
      if (isPlaying) {
        bgMusicRef.current.pause();
      } else {
        bgMusicRef.current.play().catch((e) => console.log('Audio blocked', e));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const playClick = () => {
    if (clickSfxRef.current) {
      clickSfxRef.current.currentTime = 0;
      clickSfxRef.current.play().catch(() => {});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabledByTime) return;
    if (isAuthenticated && user?.id && inviterId && user.id === inviterId) {
      toast.error('本人不可邀请本人参与');
      return;
    }
    if (!name || !phone) {
      toast.error('请填写完整信息');
      return;
    }
    const trimmedPhone = phone.trim();
    if (!CN_MOBILE_REGEX.test(trimmedPhone)) {
      toast.error('手机号输入错误');
      return;
    }
    if (!inviterId) {
      toast.error(INVITER_REQUIRED_MSG);
      return;
    }
    if (!activityId) {
      toast.error('缺少活动信息，请通过正确的链接参与。');
      return;
    }

    playClick();
    setIsSubmitting(true);

    try {
      await createByInvitation.mutateAsync({
        activityId,
        inviterId,
        name,
        gender: genderToApi(gender),
        mobile: trimmedPhone,
        extra: { chinaGolds, germanyGolds },
      });
      toast.success('提交成功，谢谢参与！');
    } catch (err) {
      // CONFLICT 等错误由 createProviders 的 MutationCache.onError 统一处理
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-background">
      <style>{`
        @keyframes olympic-ai-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .animate-olympic-ai-pulse {
          animation: olympic-ai-pulse 2s ease-in-out infinite;
        }
      `}</style>
      {/* 头部导航 */}
      <div className="sticky top-0 z-10 w-full">
        <div className="relative w-full">
          {activity?.coverImage && (
            <img
              src={activity.coverImage}
              alt="2026冬奥金牌猜想"
              className="w-full h-auto object-cover"
            />
          )}
          <button
            onClick={toggleMusic}
            className="absolute top-3 right-3 p-2 bg-white/80 hover:bg-white/90 rounded-full shadow-md transition-colors"
            type="button"
            aria-label={isPlaying ? '关闭音乐' : '播放音乐'}
          >
            {isPlaying ? (
              <Volume2 className="w-5 h-5 text-gray-700" />
            ) : (
              <VolumeX className="w-5 h-5 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      <div className="container mx-auto p-4 space-y-4">
        {/* Tab切换 */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            playClick();
            setActiveTab(value as 'guess' | 'rules' | 'winners');
          }}
          className="w-full"
        >
          <TabList className="w-full grid grid-cols-3">
            <Tab value="guess" className="flex-1">
              我要竞猜
            </Tab>
            <Tab value="rules" className="flex-1">
              抽奖安排
            </Tab>
            <Tab value="winners" className="flex-1">
              获奖名单
            </Tab>
          </TabList>

          <TabPanel value="guess" className="mt-4">
            <div className="space-y-4">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Medal className="w-5 h-5 text-yellow-500" />
                    开始竞猜
                  </span>
                  <Drawer open={aiDrawerOpen} onOpenChange={setAiDrawerOpen}>
                    <DrawerTrigger
                      render={(p) => (
                        <Button {...p} variant="default" size="sm" className="animate-olympic-ai-pulse">
                          参考AI预测
                        </Button>
                      )}
                    />
                    <DrawerContent>
                      <DrawerHeader>
                        <DrawerHeading>AI预测中德两国2026冬奥会金牌数</DrawerHeading>
                      </DrawerHeader>
                      <div className="px-4 pb-6 space-y-4">
                        <div className="relative w-full overflow-auto rounded-md border">
                          <table className="w-full caption-bottom text-sm">
                            <thead className="[&_tr]:border-b">
                              <tr className="border-b transition-colors hover:bg-default-100/50">
                                <th className="h-12 px-4 text-left align-middle font-medium text-default-500">AI</th>
                                <th className="h-12 px-4 text-left align-middle font-medium text-default-500">中国</th>
                                <th className="h-12 px-4 text-left align-middle font-medium text-default-500">德国</th>
                              </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-b-0">
                              {AI_PREDICTION_ROWS.map((row) => (
                                <tr key={row.ai} className="border-b transition-colors hover:bg-default-100/50">
                                  <td className="p-4 align-middle">{row.ai}</td>
                                  <td className="p-4 align-middle">{row.china}</td>
                                  <td className="p-4 align-middle">{row.germany}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-sm text-default-500">
                          <span className="font-bold mr-1">提示词：</span>请根据可靠的数据预测中国及德国2026年米兰冬奥会金牌数。
                        </p>
                      </div>
                    </DrawerContent>
                  </Drawer>
                </CardTitle>
                <p className="text-sm text-default-500 mt-1">
                  参与即可获抽奖机会，若猜中额外获抽大奖机会
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 中国队预测 */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="flex items-center gap-2">
                        <img
                          src={chinaFlag}
                          alt="中国国旗"
                          className="w-5 h-5 object-contain"
                        />
                        中国健儿夺金预测
                      </Label>
                      <span className="text-2xl font-bold text-red-600">
                        {chinaGolds} 枚
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={30}
                      value={[chinaGolds]}
                      onValueChange={(value) => setChinaGolds(Array.isArray(value) ? value[0] : value)}
                      step={1}
                    />
                  </div>

                  {/* 德国队预测 */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="flex items-center gap-2">
                        <img
                          src={germanFlag}
                          alt="德国国旗"
                          className="w-5 h-5 object-contain"
                        />
                        德国健儿夺金预测
                      </Label>
                      <span className="text-2xl font-bold text-gray-900">
                        {germanyGolds} 枚
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={30}
                      value={[germanyGolds]}
                      onValueChange={(value) => setGermanyGolds(Array.isArray(value) ? value[0] : value)}
                      step={1}
                    />
                  </div>

                  {/* 个人信息 */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="block mb-2.5">尊姓大名</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="请输入您的姓名"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label className="block mb-2.5">尊称</Label>
                      <div className="flex gap-2">
                        {Object.values(Gender).map((g) => (
                          <Button
                            key={g}
                            type="button"
                            variant={gender === g ? 'default' : 'outline'}
                            className="flex-1"
                            onClick={() => {
                              playClick();
                              setGender(g);
                            }}
                          >
                            {g}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone" className="block mb-2.5">手机号</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="输入手机号，中奖时以联系您"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || submitDisabledByTime}
                    className="w-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner className="inline-block mr-2" />
                        提交中...
                      </>
                    ) : notStarted ? (
                      '活动未开始'
                    ) : ended ? (
                      '活动已结束'
                    ) : (
                      '即刻提交，参与竞猜赢大奖'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

          {/* 往届金牌参考 */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-yellow-500" />
                往届夺金参考
              </CardTitle>
            </CardHeader>
            <CardContent>
              <HistoryChart />
            </CardContent>
          </Card>
            </div>
          </TabPanel>

          <TabPanel value="rules" className="mt-4">
            {/* 抽奖规则：与 LotteryRulesPage 一致，由活动 ID（URL）拉取奖品与规则 */}
            {(prizesLoading || rulesLoading) ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="w-8 h-8" />
              </div>
            ) : (
              <div className="space-y-6">
                {prizes && prizes.length > 0 && (
                  <div className="w-full px-0 py-4 bg-background">
                    <Carousel
                      className="w-full"
                      opts={{ align: 'start', loop: true }}
                      plugins={[prizesAutoplay.current]}
                    >
                      <Carousel.Content>
                        {prizes.map((prize) => (
                          <Carousel.Item key={prize.id} className="basis-full">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="w-full aspect-square max-w-xs rounded-lg overflow-hidden bg-default-100">
                                {prize.imageUrl ? (
                                  <img
                                    src={prize.imageUrl}
                                    alt={prize.name || '奖品图片'}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-default-500">
                                    暂无图片
                                  </div>
                                )}
                              </div>
                              <div className="text-center space-y-1">
                                <p className="text-base font-semibold">{prize.name || '未命名奖品'}</p>
                                {prize.quantity != null && (
                                  <p className="text-sm text-default-500">{prize.quantity}个</p>
                                )}
                              </div>
                            </div>
                          </Carousel.Item>
                        ))}
                      </Carousel.Content>
                      {prizes.length > 1 ? (
                        <Carousel.Dots
                          className="mt-4 flex justify-center gap-2"
                          renderDot={({ index, isSelected }) => (
                            <span
                              key={index}
                              className={`inline-block h-2 rounded-full transition-all ${
                                isSelected ? 'w-6 bg-primary' : 'w-2 bg-default-400/30'
                              }`}
                              aria-hidden
                            />
                          )}
                        />
                      ) : null}
                    </Carousel>
                  </div>
                )}
                {groupedRules.length > 0 && (
                  <div className="px-0 pb-4">
                    <LotteryRulesTimeline groups={groupedRules} />
                  </div>
                )}
                {!prizesLoading && !rulesLoading && (!prizes || prizes.length === 0) && (!lotteryRules || lotteryRules.length === 0) && (
                  <div className="flex flex-col items-center justify-center py-12 p-4">
                    <p className="text-default-500 text-center">暂无抽奖规则</p>
                  </div>
                )}
              </div>
            )}
          </TabPanel>

          <TabPanel value="winners" className="mt-4">
            {/* 获奖名单：搜索 + 列表 + 分页 */}
            <div className="space-y-4">
              <form onSubmit={handleWinnersSearchSubmit} className="flex gap-2">
                <Input
                  placeholder="搜索手机号（11 位）"
                  value={winnersKeywordInput}
                  onChange={(e) => setWinnersKeywordInput(e.target.value)}
                  className="flex-1"
                  maxLength={11}
                  inputMode="numeric"
                  autoComplete="off"
                />
                <Button type="submit" variant="default">
                  搜索
                </Button>
                {winnersKeywordTrimmed ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setWinnersKeyword('');
                      setWinnersKeywordInput('');
                      setWinnersPage(1);
                    }}
                  >
                    返回
                  </Button>
                ) : null}
              </form>

              {winnersSearchResultState === 'loading' ? (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              ) : winnersSearchResultState === 'not_registered' ? (
                <Card className="shadow-none">
                  <CardContent>
                    <div className="text-center py-12 text-default-500 text-sm">
                      您搜索的手机号没有注册本次活动。
                    </div>
                  </CardContent>
                </Card>
              ) : winnersSearchResultState === 'not_winner' ? (
                <Card className="shadow-none">
                  <CardContent>
                    <div className="text-center py-12 text-default-500 text-sm">
                      尚未中奖，敬请期待下一轮抽奖
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {winnersLoading ? (
                    <div className="flex justify-center py-12">
                      <Spinner />
                    </div>
                  ) : (
                  <Card className="shadow-none">
                    <CardContent className="p-0">
                      {winnersItems.length === 0 ? (
                        <div className="text-center text-default-500 py-12 text-sm">
                          暂无获奖名单
                        </div>
                      ) : (
                        <div className="w-full overflow-x-auto">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-divider">
                                <th className="text-default-500 whitespace-nowrap px-3 py-2 text-left font-medium">
                                  姓名
                                </th>
                                <th className="text-default-500 whitespace-nowrap px-3 py-2 text-left font-medium">
                                  手机号
                                </th>
                                <th className="text-default-500 whitespace-nowrap px-3 py-2 text-left font-medium">
                                  奖品名称
                                </th>
                                <th className="text-default-500 whitespace-nowrap px-3 py-2 text-left font-medium">
                                  邀请人
                                </th>
                                <th className="text-default-500 whitespace-nowrap px-3 py-2 text-left font-medium">
                                  中奖时间
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {winnersItems.map((item: Record<string, unknown>) => {
                                const inviter = item.inviter as
                                  | { avatarUrl?: string | null; nickname?: string | null; username?: string }
                                  | null
                                  | undefined;
                                const prize = item.prize as { name?: string } | null | undefined;
                                const nameDisplay = nameWithGenderDisplay(
                                  item.name as string | undefined | null,
                                  item.gender as string | undefined | null
                                );
                                const mobileStr = (item.mobile as string) ?? '—';
                                const prizeName = prize?.name?.trim() ?? '—';
                                const inviterDisplay: string =
                                  inviter
                                    ? (inviter.nickname && inviter.nickname.trim()) || inviter.username || '—'
                                    : '—';
                                const wonAt = item.wonAt as string | Date | null | undefined;
                                const wonAtStr = wonAt ? formatDateTime(wonAt) : '—';
                                return (
                                  <tr key={String(item.id)} className="border-b border-divider">
                                    <td className="text-foreground whitespace-nowrap px-3 py-2">{nameDisplay}</td>
                                    <td className="text-foreground tabular-nums whitespace-nowrap px-3 py-2">
                                      {mobileStr}
                                    </td>
                                    <td className="text-foreground whitespace-nowrap px-3 py-2">{prizeName}</td>
                                    <td className="whitespace-nowrap px-3 py-2">
                                      {inviter ? (
                                        <div className="flex min-w-0 items-center gap-2">
                                          <Avatar className="h-7 w-7 shrink-0">
                                            <AvatarImage src={inviter.avatarUrl ?? undefined} alt="" />
                                            <AvatarFallback className="text-xs">
                                              {inviterDisplay !== '—' ? inviterDisplay.slice(0, 1) : '?'}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="text-foreground truncate">{inviterDisplay}</span>
                                        </div>
                                      ) : (
                                        <span className="text-default-500">—</span>
                                      )}
                                    </td>
                                    <td className="text-default-500 whitespace-nowrap px-3 py-2">{wonAtStr}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  )}

                  {/* 分页 */}
                  {(winnersHasPrev || winnersHasNext) && (
                    <div className="flex justify-center items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setWinnersPage((p) => Math.max(1, p - 1))}
                        disabled={!winnersHasPrev}
                        className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        上一页
                      </button>
                      <span className="text-sm text-default-500">
                        {winnersPage} / {winnersTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setWinnersPage((p) => p + 1)}
                        disabled={!winnersHasNext}
                        className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        下一页
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </div>
    </>
  );
}

